import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import TenantService from '../services/tenant.service';
import type { CreateTenant, UpdateTenant, Tenant, BillingProvisionRequest } from '@policy-tracker/types';
import { ENTITLEMENT_QUERY_KEYS } from './useEntitlements';

export const TENANT_QUERY_KEYS = {
    all: ['tenants'] as const,
    lists: () => [...TENANT_QUERY_KEYS.all, 'list'] as const,
    list: (params?: { limit?: number; nextToken?: string }) => [...TENANT_QUERY_KEYS.lists(), params] as const,
    details: () => [...TENANT_QUERY_KEYS.all, 'detail'] as const,
    detail: (id: string) => [...TENANT_QUERY_KEYS.details(), id] as const,
};

/**
 * Hook to fetch paginated tenants
 */
export function useTenants(params?: { limit?: number; nextToken?: string }) {
    return useQuery({
        queryKey: TENANT_QUERY_KEYS.list(params),
        queryFn: () => TenantService.listTenants(params),
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
}

/**
 * Hook to fetch a single tenant by ID
 */
export function useTenant(id: string, enabled = true) {
    return useQuery({
        queryKey: TENANT_QUERY_KEYS.detail(id),
        queryFn: () => TenantService.getTenant(id),
        enabled: enabled && !!id,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
}

/**
 * Hook to create a new tenant
 */
export function useCreateTenant() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (tenant: CreateTenant) => TenantService.createTenant(tenant),
        onSuccess: (newTenant: Tenant) => {
            // Invalidate and refetch tenants list
            queryClient.invalidateQueries({ queryKey: TENANT_QUERY_KEYS.lists() });

            // Add the new tenant to the cache
            queryClient.setQueryData(TENANT_QUERY_KEYS.detail(newTenant.tenantId), newTenant);
        },
    });
}

/**
 * Hook to update an existing tenant
 */
export function useUpdateTenant() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, tenant }: { id: string; tenant: UpdateTenant }) => TenantService.updateTenant(id, tenant),
        onSuccess: (updatedTenant: Tenant) => {
            // Invalidate and refetch tenants list
            queryClient.invalidateQueries({ queryKey: TENANT_QUERY_KEYS.lists() });

            // Update the tenant in the cache
            queryClient.setQueryData(TENANT_QUERY_KEYS.detail(updatedTenant.tenantId), updatedTenant);
        },
    });
}

/**
 * Hook to provision Stripe billing (customer and optionally subscription)
 * for a tenant via the TM admin endpoint added in #2071.
 *
 * Used by the TM Provision Billing per-row action for legacy/pre-checkout
 * tenants. On success, invalidates both the entitlements detail (the
 * `hasBillingAccount` flag flips to `true`) and the tenants list (the row
 * may surface differently once billing is wired up).
 */
export function useProvisionBilling() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ tenantId, body }: { tenantId: string; body: BillingProvisionRequest }) => TenantService.provisionBilling(tenantId, body),
        onSuccess: (_, { tenantId }) => {
            queryClient.invalidateQueries({ queryKey: ENTITLEMENT_QUERY_KEYS.detail(tenantId) });
            queryClient.invalidateQueries({ queryKey: TENANT_QUERY_KEYS.lists() });
        },
    });
}

/**
 * Hook to delete a tenant
 */
export function useDeleteTenant() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => TenantService.deleteTenant(id),
        onSuccess: (_: void, deletedId: string) => {
            // Invalidate and refetch tenants list
            queryClient.invalidateQueries({ queryKey: TENANT_QUERY_KEYS.lists() });

            // Remove the tenant from the cache
            queryClient.removeQueries({ queryKey: TENANT_QUERY_KEYS.detail(deletedId) });
        },
    });
}
